import { optimizeCompanyFunding, type CompanyFundingOptimization, type FundingOptimizationSnapshot, type FundingStrategyResult, type RecommendedFunding } from "./companyFundingOptimizer";

export interface AutomaticFundingRecommendation extends Omit<RecommendedFunding, "eligibility"> {
  eligibility: "confirmed" | "conditional";
  /** Conditions travel with each row so an illustration cannot look actionable in a compact view. */
  conditions: string[];
}
export interface AutomaticFundingStrategy extends Omit<FundingStrategyResult, "recommendations"> {
  recommendations: AutomaticFundingRecommendation[];
}
export interface AutomaticFundingEvaluation extends Omit<CompanyFundingOptimization, "strategies"> {
  strategies: AutomaticFundingStrategy[];
  executionAllowed: false;
  evidenceStatus: "verified-sources" | "conditional" | "invalid";
  assumptions: string[];
}

/**
 * Pure, non-executable illustration adapter. It does not change the strict evaluator,
 * grant permissions, infer GC entitlement, or create financial records. Any optional
 * hypothetical snapshot must be assembled separately from canonical numerical sources;
 * its existence labels the entire answer conditional, even if no borrowing is chosen.
 * Contradictory sources must be passed as blocking issues, never as assumptions.
 */
export function evaluateAutomaticFunding(
  snapshot: FundingOptimizationSnapshot,
  assumptions: readonly string[],
  blockingIssues: readonly string[] = [],
  hypotheticalSnapshot?: FundingOptimizationSnapshot,
  conditionsByOpportunity: Readonly<Record<string, readonly string[]>> = {},
): AutomaticFundingEvaluation {
  const conditions = [...new Set(assumptions)];
  const invalid = [...new Set(blockingIssues)];
  if (hypotheticalSnapshot) {
    for (const opportunity of hypotheticalSnapshot.opportunities) {
      const purpose = opportunity.purpose ?? "owner-distribution";
      const canonical = snapshot.facilities.find(facility => facility.terms.id === opportunity.facilityId);
      if (canonical?.permissions.permissions.some(permission => permission.purpose === purpose && permission.projectId === opportunity.projectId && permission.status === "prohibited")) {
        invalid.push(`${opportunity.id}: an explicitly prohibited canonical use cannot become a hypothetical funding opportunity.`);
      }
    }
  }
  if (invalid.length) return {
    planningOnly: true, executionAllowed: false, evidenceStatus: "invalid", assumptions: conditions,
    status: "unresolved", strategies: [], recommendedStrategy: snapshot.preferences.strategy, issues: invalid,
  };
  const evaluated = structuredClone(hypotheticalSnapshot ?? snapshot);
  if (hypotheticalSnapshot) conditions.push("Hypothetical funding permissions are not lender authorization. This illustration cannot be executed.");
  if (evaluated.openingUnrestrictedCashCents === null) {
    evaluated.openingUnrestrictedCashCents = 0;
    conditions.push("Opening unrestricted company cash is unknown. This illustration assumes zero opening cash; it does not verify company cash or solvency.");
  }
  if (!evaluated.baselineCoverageConfirmed) {
    evaluated.baselineCoverageConfirmed = true;
    conditions.push("This illustration includes recorded obligations only. Completeness of accounts, taxes, debt service and remaining project costs is not confirmed.");
  }
  const uniqueConditions = [...new Set(conditions)];
  const result = optimizeCompanyFunding(evaluated);
  const conditional = uniqueConditions.length > 0;
  return {
    ...result, executionAllowed: false,
    evidenceStatus: conditional ? "conditional" : "verified-sources",
    assumptions: uniqueConditions,
    status: conditional && result.status === "feasible" ? "conditional" : result.status,
    issues: [...new Set([...result.issues, ...uniqueConditions])],
    strategies: result.strategies.map(strategy => ({
      ...strategy,
      status: conditional && strategy.status === "feasible" ? "conditional" : strategy.status,
      issues: [...new Set([...strategy.issues, ...uniqueConditions])],
      recommendations: strategy.recommendations.map(recommendation => ({
        ...recommendation,
        eligibility: conditional ? "conditional" : "confirmed",
        conditions: [...new Set([...uniqueConditions, ...(conditionsByOpportunity[recommendation.opportunityId] ?? [])])],
      })),
    })),
  };
}
